import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Building2, MapPin, Phone, User, Target } from 'lucide-react';
import { useAuth } from '../AuthContext';

const Branches = () => {
  const { isAdmin } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    contact_person: '',
    contact_phone: '',
    // SVEP Block Targets
    target_entrepreneurs: 0,
    target_enterprises: 0,
    target_cef_loans: 0,
    target_cef_amount: 0,
    target_cif_loans: 0,
    target_cif_amount: 0,
    target_bank_loans: 0,
    target_bank_amount: 0,
  });

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
    } catch (error) {
      toast.error('Failed to fetch branches');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingBranch) {
        await api.put(`/branches/${editingBranch.id}`, formData);
        toast.success('Branch updated successfully');
      } else {
        await api.post('/branches', formData);
        toast.success('Branch created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save branch');
    }
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch.name,
      code: branch.code,
      address: branch.address || '',
      contact_person: branch.contact_person || '',
      contact_phone: branch.contact_phone || '',
      target_entrepreneurs: branch.target_entrepreneurs || 0,
      target_enterprises: branch.target_enterprises || 0,
      target_cef_loans: branch.target_cef_loans || 0,
      target_cef_amount: branch.target_cef_amount || 0,
      target_cif_loans: branch.target_cif_loans || 0,
      target_cif_amount: branch.target_cif_amount || 0,
      target_bank_loans: branch.target_bank_loans || 0,
      target_bank_amount: branch.target_bank_amount || 0,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (branchId) => {
    if (!window.confirm('Are you sure you want to delete this branch?')) return;

    try {
      await api.delete(`/branches/${branchId}`);
      toast.success('Branch deleted successfully');
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete branch');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      address: '',
      contact_person: '',
      contact_phone: '',
      target_entrepreneurs: 0,
      target_enterprises: 0,
      target_cef_loans: 0,
      target_cef_amount: 0,
      target_cif_loans: 0,
      target_cif_amount: 0,
      target_bank_loans: 0,
      target_bank_amount: 0,
    });
    setEditingBranch(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="branches-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Branch Management</h1>
          <p className="text-lg text-slate-600">Manage BRC branches across different locations</p>
        </div>

        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-branch-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Branch
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingBranch ? 'Edit Branch' : 'Add New Branch'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="name">Branch Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Wangjing Branch"
                      required
                      data-testid="branch-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="code">Branch Code *</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., BR001"
                      required
                      disabled={!!editingBranch}
                      data-testid="branch-code-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone">Contact Phone</Label>
                    <Input
                      id="contact_phone"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="e.g., +91 9876543210"
                      data-testid="branch-phone-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="contact_person">Contact Person</Label>
                    <Input
                      id="contact_person"
                      value={formData.contact_person}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      placeholder="e.g., John Doe"
                      data-testid="branch-contact-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Full address"
                      data-testid="branch-address-input"
                    />
                  </div>
                </div>

                {/* SVEP Block Targets Section */}
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-emerald-700" />
                    SVEP Block Targets
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="target_entrepreneurs">Target Entrepreneurs</Label>
                      <Input
                        id="target_entrepreneurs"
                        type="number"
                        min="0"
                        value={formData.target_entrepreneurs}
                        onChange={(e) => setFormData({ ...formData, target_entrepreneurs: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                        data-testid="target-entrepreneurs-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="target_enterprises">Target Enterprises</Label>
                      <Input
                        id="target_enterprises"
                        type="number"
                        min="0"
                        value={formData.target_enterprises}
                        onChange={(e) => setFormData({ ...formData, target_enterprises: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                        data-testid="target-enterprises-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="target_cef_loans">Target CEF Loans (Count)</Label>
                      <Input
                        id="target_cef_loans"
                        type="number"
                        min="0"
                        value={formData.target_cef_loans}
                        onChange={(e) => setFormData({ ...formData, target_cef_loans: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                        data-testid="target-cef-loans-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="target_cef_amount">Target CEF Amount (Rs.)</Label>
                      <Input
                        id="target_cef_amount"
                        type="number"
                        min="0"
                        value={formData.target_cef_amount}
                        onChange={(e) => setFormData({ ...formData, target_cef_amount: parseFloat(e.target.value) || 0 })}
                        placeholder="0"
                        data-testid="target-cef-amount-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="target_cif_loans">Target CIF Loans (Count)</Label>
                      <Input
                        id="target_cif_loans"
                        type="number"
                        min="0"
                        value={formData.target_cif_loans}
                        onChange={(e) => setFormData({ ...formData, target_cif_loans: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                        data-testid="target-cif-loans-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="target_cif_amount">Target CIF Amount (Rs.)</Label>
                      <Input
                        id="target_cif_amount"
                        type="number"
                        min="0"
                        value={formData.target_cif_amount}
                        onChange={(e) => setFormData({ ...formData, target_cif_amount: parseFloat(e.target.value) || 0 })}
                        placeholder="0"
                        data-testid="target-cif-amount-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="target_bank_loans">Target Bank Loans (Count)</Label>
                      <Input
                        id="target_bank_loans"
                        type="number"
                        min="0"
                        value={formData.target_bank_loans}
                        onChange={(e) => setFormData({ ...formData, target_bank_loans: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                        data-testid="target-bank-loans-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="target_bank_amount">Target Bank Amount (Rs.)</Label>
                      <Input
                        id="target_bank_amount"
                        type="number"
                        min="0"
                        value={formData.target_bank_amount}
                        onChange={(e) => setFormData({ ...formData, target_bank_amount: parseFloat(e.target.value) || 0 })}
                        placeholder="0"
                        data-testid="target-bank-amount-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="save-branch-btn"
                  >
                    {editingBranch ? 'Update Branch' : 'Create Branch'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Branches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {branches.map((branch) => (
          <div
            key={branch.id}
            data-testid={`branch-card-${branch.id}`}
            className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="p-1.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                <Building2 className="w-4 h-4" />
              </div>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800 border border-blue-200">
                {branch.code}
              </span>
            </div>

            <h3 className="text-sm font-semibold text-slate-900 mb-1.5">{branch.name}</h3>

            <div className="space-y-1 text-[11px] text-slate-600">
              {branch.address && (
                <div className="flex items-start gap-1.5">
                  <MapPin className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-1">{branch.address}</span>
                </div>
              )}
              {branch.contact_person && (
                <div className="flex items-center gap-1.5">
                  <User className="w-3 h-3 text-slate-400" />
                  <span>{branch.contact_person}</span>
                </div>
              )}
              {branch.contact_phone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="w-3 h-3 text-slate-400" />
                  <span>{branch.contact_phone}</span>
                </div>
              )}
            </div>

            {isAdmin() && (
              <div className="flex gap-1.5 mt-2 pt-2 border-t border-slate-100">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(branch)}
                  className="flex-1 h-7 text-[10px]"
                  data-testid={`edit-branch-${branch.id}`}
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(branch.id)}
                  className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 h-7 text-[10px]"
                  data-testid={`delete-branch-${branch.id}`}
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        ))}

        {branches.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Branches Yet</h3>
            <p className="text-slate-500 mb-4">Create your first branch to get started</p>
            {isAdmin() && (
              <Button
                onClick={() => setDialogOpen(true)}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Branch
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Branches;
